import { useCallback, useEffect, useMemo } from 'react';
import { StaleAuthError } from '../api/client';

/** A read belongs to one mounted screen/query and only its newest request may publish. */
export function useScreenRequest(queryKey) {
  const scope = useMemo(() => ({ queryKey, live: true, revision: 0 }), [queryKey]);
  useEffect(() => {
    scope.live = true;
    return () => { scope.live = false; scope.revision += 1; };
  }, [scope]);

  const request = useCallback(async (read, { success, error, settled }) => {
    if (!scope.live) return;
    const revision = ++scope.revision;
    const isCurrentRequest = () => scope.live && scope.revision === revision;
    try {
      const data = await read({ isCurrentRequest });
      if (isCurrentRequest()) success(data);
    } catch (err) {
      if (isCurrentRequest() && !(err instanceof StaleAuthError)) error?.(err);
    } finally {
      if (isCurrentRequest()) settled?.();
    }
  }, [scope]);

  // Writes keep their transport alive, but their answer still belongs to this screen.
  const isCurrentScreen = useCallback(() => scope.live, [scope]);
  return { read: request, isCurrentScreen };
}
